import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useAccessToken } from '../hooks/useAccessToken';
import { API_ENDPOINTS } from '../config';
import { useUser } from '../UserContext';

export const ClientContext = createContext();

export function useClient() {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}

export function ClientProvider({ children }) {
  const { getToken } = useAccessToken();
  const { userProfile, orgProfile, refreshProfile, subscriptionState } = useUser();
  
  const [currentClient, setCurrentClient] = useState(() => {
    const cached = sessionStorage.getItem('currentClient');
    return cached ? JSON.parse(cached) : null;
  });
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch single client by ID
  const fetchClient = useCallback(async (clientId) => {
    try {
      const token = await getToken();

      const res = await fetch(
        `${API_ENDPOINTS.api}/clients/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.ok) {
        const client = await res.json();
        setCurrentClient(client);
        sessionStorage.setItem('currentClient', JSON.stringify(client));
        sessionStorage.setItem('currentClientId', clientId);
        return client;
      } else {
        console.error('Error fetching client:', res.status);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
    }
  }, [getToken]);

  // Fetch all clients for consultant
  const fetchClients = useCallback(async () => {
    console.log('📞 fetchClients called, userProfile.id:', userProfile?.id);
    if (!userProfile?.id) {
      console.warn('⚠️ fetchClients aborted - no userProfile.id');
      return;
    }
    
    setLoading(true);
    try {
      const token = await getToken();
      
      const url = `${API_ENDPOINTS.api}/clients?consultantId=${userProfile.id}`;
      console.log('🌐 Fetching clients from:', url);

      const res = await fetch(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('📡 GetClients response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('📋 Fetched clients:', data.clients);
        console.log('📋 Number of clients:', data.clients?.length || 0);
        setClients(data.clients || []);
      } else {
        console.error('Error fetching clients:', res.status);
        const errorText = await res.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id, getToken]);

  // Load last viewed client from session storage
  const loadLastViewedClient = useCallback(() => {
    const lastClientId = sessionStorage.getItem('currentClientId');
    if (lastClientId && !currentClient) {
      fetchClient(lastClientId);
    }
  }, [currentClient, fetchClient]);

  // Load clients for consultant on mount
  useEffect(() => {
    const accountType = orgProfile?.accountType || '';
    const currentTier = subscriptionState?.currentTier || '';
    console.log('🔍 ClientContext accountType check:', { accountType, currentTier });
    console.log('🔍 Is consultant?', accountType.startsWith('consultant') || currentTier.startsWith('consultant'));
    
    // Check both accountType (for paid) and currentTier (for trial)
    const isConsultant = accountType.startsWith('consultant') || currentTier.startsWith('consultant');
    
    if (isConsultant) {
      console.log('✅ Loading clients for consultant');
      fetchClients();
      loadLastViewedClient();
    } else {
      console.log('⚠️ NOT loading clients - not a consultant account');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgProfile?.accountType, subscriptionState?.currentTier, fetchClients]);

  const switchClient = async (clientId) => {
    console.log('🔄 Switching to client:', clientId);
    const client = await fetchClient(clientId);
    console.log('🔄 Switched client result:', client);
    console.log('🔄 SessionStorage after switch:', sessionStorage.getItem('currentClient'));
    
    // TODO: Update user profile's last viewed client when UpdateUserProfile endpoint is created
    // For now, session storage tracking is sufficient
    
    return client;
  };

  const createClient = async (clientData) => {
    try {
      const token = await getToken();
      
      console.log('🔍 Creating client with userProfile.id:', userProfile?.id);
      console.log('🔍 Full userProfile:', userProfile);

      const res = await fetch(`${API_ENDPOINTS.api}/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          consultantId: userProfile?.id,
          ...clientData
        })
      });

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Client created, refreshing list and profile...');
        await Promise.all([
          fetchClients(), // Refresh client list
          refreshProfile() // Refresh user profile to update usage counts
        ]);
        return data.client;
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create client');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  };

  const updateClient = async (clientId, updates) => {
    try {
      const token = await getToken();

      const res = await fetch(`${API_ENDPOINTS.api}/clients/${clientId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        const updatedClient = await res.json();
        
        // Update current client if it's the one being updated
        if (currentClient?.clientId === clientId) {
          setCurrentClient(updatedClient);
          sessionStorage.setItem('currentClient', JSON.stringify(updatedClient));
        }
        
        // Refresh clients list
        await fetchClients();
        
        return updatedClient;
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update client');
      }
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  };

  const archiveClient = async (clientId) => {
    console.log('📦 archiveClient called with clientId:', clientId);
    try {
      console.log('🔐 Acquiring access token...');
      const token = await getToken();
      console.log('✅ Token acquired');

      const url = `${API_ENDPOINTS.api}/clients/${clientId}/archive`;
      console.log('🌐 Making POST request to:', url);
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('📡 Response status:', res.status, res.statusText);

      if (res.ok) {
        console.log('✅ Archive API call successful');
        
        // Clear current client if it's the one being archived
        if (currentClient?.clientId === clientId) {
          console.log('🔄 Clearing current client (archiving active client)');
          setCurrentClient(null);
          sessionStorage.removeItem('currentClient');
          sessionStorage.removeItem('currentClientId');
        }
        
        // Refresh clients list and user profile
        console.log('🔄 Refreshing clients list and profile...');
        await Promise.all([
          fetchClients(),
          refreshProfile() // Update usage counts
        ]);
        console.log('✅ Clients list and profile refreshed');
        
        return true;
      } else {
        console.error('❌ Archive API call failed with status:', res.status);
        const error = await res.json();
        console.error('❌ Error details:', error);
        throw new Error(error.error || 'Failed to archive client');
      }
    } catch (error) {
      console.error('❌ Error in archiveClient:', error);
      throw error;
    }
  };

  const unarchiveClient = async (clientId) => {
    console.log('📤 unarchiveClient called with clientId:', clientId);
    try {
      const token = await getToken();

      const url = `${API_ENDPOINTS.api}/clients/${clientId}/unarchive`;
      console.log('🌐 Making POST request to:', url);
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('📡 Response status:', res.status, res.statusText);

      if (res.ok) {
        console.log('✅ Unarchive API call successful');
        
        // Refresh clients list and user profile
        console.log('🔄 Refreshing clients list and profile...');
        await Promise.all([
          fetchClients(),
          refreshProfile() // Update usage counts
        ]);
        console.log('✅ Clients list and profile refreshed');
        
        return true;
      } else {
        console.error('❌ Unarchive API call failed with status:', res.status);
        const error = await res.json();
        console.error('❌ Error details:', error);
        throw new Error(error.message || error.error || 'Failed to restore client');
      }
    } catch (error) {
      console.error('❌ Error in unarchiveClient:', error);
      throw error;
    }
  };

  const refreshClients = () => {
    fetchClients();
    if (currentClient) {
      fetchClient(currentClient.clientId);
    }
  };

  return (
    <ClientContext.Provider value={{
      currentClient,
      clients,
      loading,
      switchClient,
      createClient,
      updateClient,
      archiveClient,
      unarchiveClient,
      refreshClients,
      setCurrentClient
    }}>
      {children}
    </ClientContext.Provider>
  );
}
